import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { projectsApi } from '../../utils/projectsApi';
import { NarrativeForm } from '../../components/Forms/NarrativeForm';
import type { NarrativeFormData } from '../../components/Forms/types';
import { API } from '../../API';
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import { mapThreatsStatus } from '../../utils/threats';
import { toApiAffiliationTypes, getAffiliationTypeOptionsByValues } from '../../utils/affiliationTypes';
import { languageNamesMapping, languageNamesMappingUkrainian } from '../../utils/languages';
import { sentimentNameMapping } from '../../utils/sentiment';
import { mapIoCTypeOptions } from '../../utils/actors';
import { topicModelingTranslationLanguageOptions } from '../../utils/languages';
import { createTimeoutSignal } from '../../utils/common';
import { showRequestError } from '../../utils/requestError';
import { toast } from 'react-toastify';
import { Loader } from '../../components/Loader/Loader';
import { getCollectCommentsFormFields, getCollectCommentsParameters } from '../../utils/collectComments';

export default function NarrativeEditPageNew( {isThreat = false} ) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { narrativeId } = useParams();
  const [currentUser] = useCurrentUser();
  const [currentQuota] = useCurrentQuota();
  const [initialData, setInitialData] = useState<Partial<NarrativeFormData> | null>(null);
  const [loading, setLoading] = useState(true);
  const [narrative, setNarrative] = useState<any>(null);
  const workspaceId = parseInt(localStorage.getItem('workspace_id') || '0');

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showTopicModelingBeta = featureFlags?.includes('topic_modeling_beta_pipelines');
  const showStoriesParameters = featureFlags?.includes('show_stories_parameters');

  const aspectBasedSentimentOptions = [
    { label: t('Sentiment towards a topic or narrative'), value: 'SENTIMENT_STATEMENT' },
    { label: t('Sentiment towards person'), value: 'SENTIMENT_PERSON' },
    { label: t('Sentiment towards organization'), value: 'SENTIMENT_ORGANIZATION' },
    { label: t('Sentiment towards country'), value: 'SENTIMENT_COUNTRY' },
    { label: t('Custom sentiment option'), value: 'SENTIMENT_CUSTOM' },
  ];

  let topicModelingEntityOptions = [
    { label: t('Events only'), value: 'EVENTS_AND_OPINIONS_V2' },
  ];

  if (showTopicModelingBeta) {
    topicModelingEntityOptions.push({
      label: t('Events only Beta'),
      value: 'EVENTS_ONLY_BETA',
    });
  }

  if (currentUser?.is_super_admin) {
    topicModelingEntityOptions.push({
      label: t('Events & opinions legacy'),
      value: 'EVENTS_AND_OPINIONS',
    });
  }

  const dateRangePresetOptions = [
    { value: 'FIXED', label: t('Fixed date range') },
    { value: 'YESTERDAY', label: t('Yesterday') },
    { value: 'YESTERDAY_TODAY', label: t('Yesterday - Today') },
    { value: 'LAST_7_DAYS', label: t('Last 7 days') },
    { value: 'CURRENT_WEEK', label: t('Current week') },
    { value: 'LAST_WEEK', label: t('Last week') },
  ];

  // let topicModelingTranslationLanguageOptions = [
  //   { label: t('English'), value: 'en' },
  //   { label: t('Ukrainian'), value: 'uk' },
  //   { label: t('French'), value: 'fr' },
  //   { label: t('Polish'), value: 'pl' },
  //   { label: t('Romanian'), value: 'ro' },
  //   { label: t('German'), value: 'de' },
  // ];

   const evolutionTabsOptions = [
    { value: 'publications', label: t('Publications') },
    { value: 'impressions', label: t('Views') },
    { value: 'engagement', label: t('Engagement') },
  ];

  const evolutionBreakdownsOptions = [
    { value: 'aggregation', label: t('Stats aggregation') },
    { value: 'breakdown', label: t('Stats breakdown') },
  ];

  const summaryPanelOptions = [
    { value: 'standard', label: t('Standard') },
    { value: 'custom', label: t('Custom') },
  ];

  const eventCardOptions = [
    { value: 'rating', label: t('Rating') },
    { value: 'messages', label: t('Publications') },
  ];

  const opinionCardOptions = [
    { value: 'rating', label: t('Rating') },
    { value: 'messages', label: t('Publications') },
  ];

  const sentimentBarOptions = [
    { value: 'messages', label: t('Publications') },
    { value: 'posts', label: t('Posts') },
  ];

  const topActorsOptions = [
    { value: 'audience', label: t('Sort by audience') },
    { value: 'messages_count', label: t('Sort by number of publications') },
  ];

  const getWorkspacesList = () => {
      if (!currentUser?.is_super_admin) {
        return;
      }
  
      if (!narrative?.workspace_ids || narrative?.workspace_ids?.length === 0) {
        return;
      }
  
      const urlParams = new URLSearchParams();
  
      narrative?.workspace_ids?.forEach((id: number | string) => urlParams.append('ids', (id).toString()));
  
      API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
        (data) => {
          return data.objects.map((w: { id: number | string; name: string }) => {
              return { value: w.id, label: w.name };
            });
        }
      );
    };

  useEffect(() => {
    const loadNarrative = async () => {
      try {
        const narrative = await API.fetch('GET', `/API/v1/narratives/${narrativeId}`);
        setNarrative(narrative);
        
        const params = narrative.parameters || {};
        
        let currentNarrativeType = 0;
        if (narrative.is_manual) {
          currentNarrativeType = 1; // MANUAL
        } else if (narrative.type === 'TOPIC_MODELING') {
          currentNarrativeType = 2; // TOPIC_MODELING
        }

        const formData: Partial<NarrativeFormData> = {
          // Basic Info
          name: narrative.name || '',
          description: narrative.description || '',
          isActive: narrative.is_active ?? true,
          isManuallyManaged: narrative.is_manually_managed ?? false,
          currentNarrativeType,
          reviewStatus: null, 
          keywords: null,
          startDate: null,
          endDate: null,
          isCaseOpen: true,
          isMultilang: false,
          nativeLang: { value: '', label: t('Detect Automatically') },
          sourceTypes: [],
          contentTypes: [],
          languages: [],
          languagesExclude: false,
          collectCommentsToPosts: false,
          collectCommentsMode: 'ALL',
          collectCommentsPerPostLimit: null,
          collectCommentsSamplePercent: null,
          countries: [],
          countriesExclude: false,
          sourceGroups: [],
          sourceGroupsExclude: false,
          sources: [],
          sourcesExclude: false,
          geoGroup: [],
          geoGroupExclude: false,
          tags: [],
          tagsExclude: false,
          feeds: [],
          feedsExclude: false,
          affiliationCountries: [],
          affiliationCountriesExclude: false,
          affiliationAll: false,
          affiliationTypes: [],
          affiliationTypesExclude: false,
          discreditations: [],
          discreditationsExclude: false,
          showBotsOnly: null,
          isAspectBasedSentimentEnabled: false,
          aspectBasedSentimentObject: null,
          aspectBasedSentimentSubject: '',
          createStories: false,
          storiesCandidatesMinThreshold: 0.8,
          storiesGroupingMaxDistance: 0.64,
          useStoriesGroupingMaxDistance: false,
          enableStoriesSummarization: true,
          storiesTranslationLanguage: null,
          storiesVectorsModel: null,
          includeCommentsToStories: false,
          includeChatMessagesToStories: false,
          topicModelingEntity: null,
          topicModelingTranslationLanguage: null,
          addDateRangeSettings: false,
          dateRangePreset: null,
          startDatePreset: null,
          endDatePreset: null,
          addDefaultSorting: false,
          defaultSorting: { fieldName: 'rating', isAscending: true },
          widgetsSettings: false,
          summaryPanel: null,
          eventCard: null,
          opinionCard: null,
          sentimentBar: [],
          geoWidget: true,
          topActors: null,
          actorTypes: true,
          evolutionChart: true,
          evolutionTabs: [],
          evolutionBreakdowns: [],
          sentiment: [],
          applySupervisedClassifier: false,
          refreshWholeCase: false,
          organizations: [],
        };

        const applyNarrativeData = async () => {
          const updatedFormData = { ...formData };

          if (narrative?.review_status) {
            const reviewStatrusOption = {
              value: narrative?.review_status,
              label: t(mapThreatsStatus[narrative?.review_status as keyof typeof mapThreatsStatus]),
            };
            updatedFormData.reviewStatus = reviewStatrusOption;
          } 

          if (params.keywords) {
            const keywordsValue = String(params.keywords);
            updatedFormData.keywords = keywordsValue;
          }

          if (params.languages) {
            updatedFormData.languages = params.languages.map((v: string) => ({
              value: v,
              label: currentUser?.language === 'UK' ? languageNamesMappingUkrainian[v as keyof typeof languageNamesMappingUkrainian] : languageNamesMapping[v as keyof typeof languageNamesMapping]
            }));
          }

          if (params.content_types) {
            updatedFormData.contentTypes = params.content_types;
          }

          if (params.start_date) {
            updatedFormData.startDate = parseISO(params.start_date);
          }

          if (params.end_date) {
            updatedFormData.isCaseOpen = false;
            updatedFormData.endDate = parseISO(params.end_date);
          } else {
            updatedFormData.isCaseOpen = true;
          }

          if (params.apply_supervised_classifier !== undefined) {
            updatedFormData.applySupervisedClassifier = params.apply_supervised_classifier;
          }

          if (params.source_types) {
            updatedFormData.sourceTypes = params.source_types;
          }

          if (params.source_group_ids) {
            try {
              const promises = params.source_group_ids.map((source_group_id: number) =>
                API.fetch('GET', `/API/v1/groups/${source_group_id}`)
              );
              const results = await Promise.all(promises);
              updatedFormData.sourceGroups = results
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((v) => ({ value: v.id, label: v.name }));
            } catch (error) {
              showRequestError(error, t('Source groups'));
            }
          }

          if (params.feed_ids) {
            try {
              const promises = params.feed_ids.map((feed_id: number) =>
                API.fetch('GET', `/API/v1/feeds?ids=${feed_id}`)
              );
              const results = await Promise.all(promises);
              updatedFormData.feeds = results
                .sort((a, b) => (a.objects[0].name > b.objects[0].name ? 1 : -1))
                .map((v) => ({ value: v.objects[0].id, label: v.objects[0].name }));
            } catch (error) {
              showRequestError(error, t('Feeds'));
            }
          }

          if (params.country_group_ids) {
            updatedFormData.geoGroup = params.country_group_ids.map((id:string) => parseInt(id));
          }

          if (Array.isArray(params.tags)) {
            updatedFormData.tags = params.tags;
          }

          if (params.source_ids?.length > 0) {
            const urlParams = new URLSearchParams();

            params.source_ids.forEach((id: number) => {
              urlParams.append('ids', id.toString())
            });

            urlParams.set('size', params.source_ids.length.toString());
            await API.fetch('GET', `/API/v2/actors?${urlParams.toString()}`).then(async (data) => {
              let selected = data.objects.map((source: { id: number | string; name: string }) => ({ value: source.id, label: source.name }));
              updatedFormData.sources = selected;
            })
            .catch((error) => showRequestError(error, t('Sources')));
          }

          if (params.source_origin_country_ids) {
            try {
              const promises = params.source_origin_country_ids.map((country_id: number) =>
                API.fetch('GET', `/API/v1/countries/${country_id}`)
              );
              const results = await Promise.all(promises);
              updatedFormData.countries = results
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((v) => ({ value: v.id, label: v.name }));
            } catch (error) {
              showRequestError(error, t('Countries'));
            }
          }

          if (params.translate_keywords_query === true) {
            updatedFormData.isMultilang = true;
            updatedFormData.nativeLang = {
              value: params.keywords_query_origin_language,
              label: languageNamesMapping[
                narrative?.parameters?.keywords_query_origin_language as keyof typeof languageNamesMapping
              ],
            };
          }

          if (params.sentiment !== null && params.sentiment?.length > 0) {
            const sentimentList = params.sentiment.map((sentiment: string) => {
              return { value: sentiment, label: sentimentNameMapping[sentiment as keyof typeof sentimentNameMapping] };
            });
            updatedFormData.sentiment = sentimentList;
          }

          updatedFormData.createStories = params.create_stories;
          updatedFormData.collectCommentsToPosts = params.collect_comments_to_posts;
          Object.assign(updatedFormData, getCollectCommentsFormFields(params));

          if (params.stories_candidates_min_threshold) {
            updatedFormData.storiesCandidatesMinThreshold = params.stories_candidates_min_threshold;
          }

          if (params.stories_grouping_max_distance) {
            updatedFormData.storiesGroupingMaxDistance = params.stories_grouping_max_distance;
            updatedFormData.useStoriesGroupingMaxDistance = true;
          }

          if (params.enable_stories_summarization !== undefined && params.enable_stories_summarization !== null) {
            updatedFormData.enableStoriesSummarization = params.enable_stories_summarization;
          }

          if (params.stories_summarization_language) {
            let langOptions = topicModelingTranslationLanguageOptions(t);
            updatedFormData.storiesTranslationLanguage = {
              value: params.stories_summarization_language,
              label: langOptions.find(
                (option) => option.value === params.stories_summarization_language
              )?.label || params.stories_summarization_language,
            };
          }
          // No saved language: leave storiesTranslationLanguage as null so
          // NarrativeForm falls back to the workspace translation language.

          if (params.source_bots === true) {
            updatedFormData.showBotsOnly = true;
          }

          if (params.source_state_affiliated_all_countries === true) {
            updatedFormData.affiliationAll = true;
          }

          if (params.source_discreditated_entity_types) {
            const IoCTypes = mapIoCTypeOptions(t);
            const selected = params.source_discreditated_entity_types.map((option: string) => 
              IoCTypes[option as keyof typeof IoCTypes]
            );
            updatedFormData.discreditations = selected;
          }

          if (params.source_state_affiliated_country_ids) {
            try {
              const promises = params.source_state_affiliated_country_ids.map((country_id: number) =>
                API.fetch('GET', `/API/v1/countries/${country_id}`)
              );
              const results = await Promise.all(promises);
              updatedFormData.affiliationCountries = results
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((v) => ({ value: v.id, label: v.name }));
            } catch (error) {
              showRequestError(error, t('Affiliation countries'));
            }
          }

          if (params.source_affiliation_types && params.source_affiliation_types.length > 0) {
            // saved cases may still hold legacy backend values; fold them into the new categories
            updatedFormData.affiliationTypes = getAffiliationTypeOptionsByValues(t, params.source_affiliation_types);
          }

          if (params.aspect_based_sentiment) {
            updatedFormData.isAspectBasedSentimentEnabled = params.aspect_based_sentiment;
          }

          if (params.aspect_based_sentiment_object) {
            updatedFormData.aspectBasedSentimentObject = aspectBasedSentimentOptions.find(
              (option) => option.value === params.aspect_based_sentiment_object
            );
          }

          if (params.aspect_based_sentiment_subject) {
            updatedFormData.aspectBasedSentimentSubject = params.aspect_based_sentiment_subject;
          }

          if (params.topic_modeling_entity) {
            updatedFormData.topicModelingEntity = topicModelingEntityOptions.find(
              (option) => option.value === params.topic_modeling_entity
            );
          }

          if (params.topic_modeling_dashboard_dates_preset) {
            let selected = dateRangePresetOptions.filter(
              (option) =>
                option.value ===
                narrative?.parameters?.topic_modeling_dashboard_dates_preset,
            );
            updatedFormData.dateRangePreset = selected[0];
            updatedFormData.addDateRangeSettings = true;
          }

          if (params.topic_modeling_dashboard_start_date) {
            updatedFormData.startDatePreset = parseISO(params.topic_modeling_dashboard_start_date);
          }

          if (params.topic_modeling_dashboard_end_date) {
            updatedFormData.endDatePreset = parseISO(params.topic_modeling_dashboard_end_date);
          }

          if (params.topic_modeling_dashboard_sort) {
            const defaultSorting = params.topic_modeling_dashboard_sort;
            updatedFormData.addDefaultSorting = true;
            updatedFormData.defaultSorting = {
              fieldName: defaultSorting.replace('-', ''),
              isAscending: !defaultSorting.startsWith('-'),
            };
          }

          if (params.topic_modeling_translate_language) {
            let langOptions = topicModelingTranslationLanguageOptions(t);
            updatedFormData.topicModelingTranslationLanguage = {
              value: params.topic_modeling_translate_language,
              label: langOptions.find(
                (option) => option.value === params.topic_modeling_translate_language
              )?.label || params.topic_modeling_translate_language,
            };
          } else {
            updatedFormData.topicModelingTranslationLanguage = {
              value: 'EN',
              label: t('English'),
            };
          }

          if (params.exclusive_filters) {
            updatedFormData.languagesExclude = params.exclusive_filters.includes('languages');
            updatedFormData.countriesExclude = params.exclusive_filters.includes('source_origin_country_ids');
            updatedFormData.sourceGroupsExclude = params.exclusive_filters.includes('source_group_ids');
            updatedFormData.sourcesExclude = params.exclusive_filters.includes('source_ids');
            updatedFormData.affiliationCountriesExclude = params.exclusive_filters.includes('source_state_affiliated_country_ids');
            updatedFormData.affiliationTypesExclude = params.exclusive_filters.includes('source_affiliation_types');
            updatedFormData.discreditationsExclude = params.exclusive_filters.includes('source_discreditated_entity_types');
            updatedFormData.feedsExclude = params.exclusive_filters.includes('feed_ids');
            updatedFormData.geoGroupExclude = params.exclusive_filters.includes('country_group_ids');
            updatedFormData.showBotsOnly = params.exclusive_filters.includes('source_bots') ? false : updatedFormData.showBotsOnly;
            updatedFormData.tagsExclude = params.exclusive_filters.includes('tags');
          }

          if (params.topic_modeling_widget_config) {
            const config = params.topic_modeling_widget_config;
            updatedFormData.widgetsSettings = true;
            
            if (config.summary_panel) {
              updatedFormData.summaryPanel = summaryPanelOptions.find((item) => item.value === config.summary_panel);
            }
            if (config.event_card) {
              updatedFormData.eventCard = eventCardOptions.find((item) => item.value === config.event_card);
            }
            if (config.opinion_card) {
              updatedFormData.opinionCard = opinionCardOptions.find((item) => item.value === config.opinion_card);
            }
            if (config.sentiment_bar) {
              updatedFormData.sentimentBar = sentimentBarOptions.filter((item) => config.sentiment_bar.includes(item.value));
            }
            if (config.geo_widget !== undefined) {
              updatedFormData.geoWidget = config.geo_widget;
            }
            if (config.top_actors) {
              updatedFormData.topActors = topActorsOptions.find((item) => item.value === config.top_actors);
            }
            if (config.actor_types !== undefined) {
              updatedFormData.actorTypes = config.actor_types;
            }
            if (config.evolution_tabs) {
              updatedFormData.evolutionTabs = evolutionTabsOptions.filter((item) => config.evolution_tabs.includes(item.value));
            }
            if (config.evolution_breakdowns) {
              updatedFormData.evolutionBreakdowns = evolutionBreakdownsOptions.filter((item) => config.evolution_breakdowns.includes(item.value));
            }
            if (config.evolution_chart !== undefined) {
              updatedFormData.evolutionChart = config.evolution_chart;
            }
          }

          updatedFormData.isManuallyManaged = narrative.is_manually_managed;

          if (params.stories_vectors_model) {
            updatedFormData.storiesVectorsModel = params.stories_vectors_model;
          }

          updatedFormData.includeCommentsToStories = params.include_comments_to_stories;
          updatedFormData.includeChatMessagesToStories = params.include_chat_messages_to_stories;

          if (currentUser?.is_super_admin) {
            const workspacesList = await getWorkspacesList();
            updatedFormData.organizations = Array.isArray(workspacesList) && workspacesList.length > 0 ? workspacesList : [];
          }

          try {
            const projects = await projectsApi.list();
            const caseId = parseInt(narrativeId as string);
            const currentProject = projects.find((p: any) => p.case_ids?.includes(caseId));
            if (currentProject) {
              updatedFormData.project = { value: currentProject.id, label: currentProject.name };
            }
          } catch (err) {
            showRequestError(err, t('Projects'));
          }

          setInitialData(updatedFormData);
          setLoading(false);
        };

        applyNarrativeData();
      } catch (error) {
        showRequestError(error, t('Case'));
        setLoading(false);
      }
    };

    if (narrativeId) {
      loadNarrative();
    }
  }, [narrativeId, t]);

  const handleEdit = async (data: NarrativeFormData) => {
    const narrativeTypes = [
      { type: 'NARRATIVE' },
      { type: 'MANUAL' }, 
      { type: 'TOPIC_MODELING' }
    ];
    
    const currentType = narrativeTypes[data.currentNarrativeType]?.type;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (isThreat && narrative?.workspace_id !== currentUser?.workspace?.id) {
      headers['workspace-id'] = narrative?.workspace_id;
    }

    let parameters: any = {};
    if (currentType === 'MANUAL') {
      parameters = null;
    }

    let excludingFilters: string[] = [];
    if (data.languagesExclude) excludingFilters.push('languages');
    if (data.countriesExclude) excludingFilters.push('source_origin_country_ids');
    if (data.sourceGroupsExclude) excludingFilters.push('source_group_ids');
    if (data.sourcesExclude) excludingFilters.push('source_ids');
    if (data.affiliationCountriesExclude) {
      if (data.affiliationAll) {
        excludingFilters.push('source_state_affiliated_all_countries');
      } else {
        excludingFilters.push('source_state_affiliated_country_ids');
      }
    }
    if (data.discreditationsExclude) excludingFilters.push('source_discreditated_entity_types');
    if (data.affiliationTypesExclude && Array.isArray(data.affiliationTypes) && data.affiliationTypes.length > 0) excludingFilters.push('source_affiliation_types');
    if (data.feedsExclude) excludingFilters.push('feed_ids');
    if (data.geoGroupExclude) excludingFilters.push('country_group_ids');
    if (data.showBotsOnly === false) excludingFilters.push('source_bots');
    if (data.tagsExclude && Array.isArray(data.tags) && data.tags.length > 0) excludingFilters.push('tags');

    if (currentType === 'TOPIC_MODELING') {
      parameters = {
        start_date: data.startDate ? format(data.startDate, 'yyyy-LL-dd') : null,
        end_date: data.endDate && !data.isCaseOpen ? format(data.endDate, 'yyyy-LL-dd') : null,
        keywords: data.keywords ? [data.keywords] : [],
        topic_modeling_dashboard_dates_preset:
          data.addDateRangeSettings && data.dateRangePreset && data.topicModelingEntity?.value !== 'OPINIONS_ONLY'
            ? data.dateRangePreset.value
            : null,
        topic_modeling_dashboard_start_date:
          data.addDateRangeSettings && data.startDatePreset &&
          data.dateRangePreset?.value === 'FIXED' &&
          data.topicModelingEntity?.value !== 'OPINIONS_ONLY'
            ? format(data.startDatePreset, 'yyyy-LL-dd')
            : null,
        topic_modeling_dashboard_end_date:
          data.addDateRangeSettings && data.endDatePreset &&
          data.dateRangePreset?.value === 'FIXED' &&
          data.topicModelingEntity?.value !== 'OPINIONS_ONLY'
            ? format(data.endDatePreset, 'yyyy-LL-dd')
            : null,
        topic_modeling_dashboard_sort: data.addDefaultSorting ? (data.defaultSorting.fieldName
          ? `${data.defaultSorting.isAscending ? '' : '-'}${data.defaultSorting.fieldName}`
          : null) : null,
        topic_modeling_translate_language: data.topicModelingTranslationLanguage ? data.topicModelingTranslationLanguage.value : null,
        stories_candidates_min_threshold: data.storiesCandidatesMinThreshold,
        stories_grouping_max_distance: data.storiesGroupingMaxDistance,
      };
    }

    if (currentType === 'NARRATIVE') {
      parameters = {
        keywords: data.keywords ? [data.keywords] : [],
        examples: [],
        start_date: data.startDate ? format(data.startDate, 'yyyy-LL-dd') : null,
        end_date: data.endDate && !data.isCaseOpen ? format(data.endDate, 'yyyy-LL-dd') : null,
        apply_supervised_classifier: data.applySupervisedClassifier,
        source_types: data.sourceTypes,
        source_group_ids: data.sourceGroups.map((v) => v.value),
        languages: data.languages.map((v) => v.value),
        source_ids: data.sources.map((v) => v.value),
        source_origin_country_ids: data.countries.map((v) => v.value),
        translate_keywords_query: data.isMultilang,
        keywords_query_origin_language: data.isMultilang && data.nativeLang ? data.nativeLang.value : '',
        sentiment: data.sentiment.map(score => score.value),
        content_types: data.contentTypes,
        create_stories: data.createStories,
        stories_candidates_min_threshold: data.createStories ? data.storiesCandidatesMinThreshold : null,
        stories_grouping_max_distance: data.createStories ? data.storiesGroupingMaxDistance : null,
        enable_stories_summarization: data.createStories ? showStoriesParameters ? data.enableStoriesSummarization : true : false,
        stories_summarization_language: data.createStories && data.enableStoriesSummarization && data.storiesTranslationLanguage ? data.storiesTranslationLanguage.value : null,
        source_state_affiliated_all_countries: data.affiliationAll,
        source_state_affiliated_country_ids: data.affiliationAll ? [] : data.affiliationCountries.map(v => v.value),
        source_affiliation_types: Array.isArray(data.affiliationTypes) ? toApiAffiliationTypes(data.affiliationTypes.map((v) => v.value)) : [],
        feed_ids: data.feeds.map(v => v.value),
        source_discreditated_entity_types: data.discreditations.map((v) => v.value),
        source_bots: data.showBotsOnly !== null ? data.showBotsOnly : null,
        aspect_based_sentiment: data.isAspectBasedSentimentEnabled,
        aspect_based_sentiment_object: data.isAspectBasedSentimentEnabled ? data.aspectBasedSentimentObject?.value : null,
        aspect_based_sentiment_subject: data.isAspectBasedSentimentEnabled ? data.aspectBasedSentimentSubject : null,
        ...getCollectCommentsParameters(data),
        exclusive_filters: excludingFilters.length > 0 ? excludingFilters : null,
        stories_vectors_model: data.storiesVectorsModel ? data.storiesVectorsModel.value : null,
        include_comments_to_stories: data.includeCommentsToStories,
        include_chat_messages_to_stories: data.includeChatMessagesToStories,
        country_group_ids: data.geoGroup?.length > 0 ? data.geoGroup : [],
        tags: data.tags?.length > 0 ? data.tags : [],
        topic_modeling_translate_language: null,
      };
    }

    let widgetsConfig = null;
    if (data.widgetsSettings) {
      widgetsConfig = {
        summary_panel: data.summaryPanel?.value || null,
        event_card: data.eventCard?.value || null,
        opinion_card: data.opinionCard?.value || null,
        sentiment_bar: data.sentimentBar?.length > 0 ? data.sentimentBar.map((v) => v.value) : null,
        geo_widget: data.geoWidget,
        top_actors: data.topActors?.value || null,
        actor_types: data.actorTypes,
        evolution_tabs: data.evolutionTabs?.length > 0 ? data.evolutionTabs.map((v) => v.value) : null,
        evolution_breakdowns: data.evolutionBreakdowns?.length > 0 ? data.evolutionBreakdowns.map((v) => v.value) : null,
        evolution_chart: data.evolutionChart,
      };
    }

    const response = await API.fetch('PATCH', `/API/v1/narratives/${narrativeId}`, headers, {
      name: data.name,
      description: data.description,
      type: currentType === 'MANUAL' ? 'NARRATIVE' : currentType,
      is_active: data.isActive,
      is_manual: currentType === 'MANUAL',
      is_threat: isThreat,
      is_created_by_ucbi_reporter: narrative?.is_created_by_ucbi_reporter,
      incident_type: narrative?.incident_type || null,
      topic: narrative?.topic || null,
      geo_scope: narrative?.geo_scope || null,
      workspace_ids: narrative?.workspace_ids || null,
      review_status: data.reviewStatus?.value,
      project_id: (data.project?.value as number) ?? null,
      ...(currentType === 'TOPIC_MODELING'
        ? {
            parameters: {
              ...narrative.parameters,
              ...parameters,
              topic_modeling_widget_config: widgetsConfig,
            },
          }
        : { parameters: parameters }),
      confirm_quota_usage: data.confirm_quota_usage,
      is_manually_managed: data.isManuallyManaged,
    }, createTimeoutSignal(120_000));

    if (!data.confirm_quota_usage) {
      if (response?.id) {
        ampli.track({
          event_type: 'Case creation',
          event_properties: {
            is_stories_applied: data.createStories,
            is_aspect_based_sentiment_applied: data.isAspectBasedSentimentEnabled,
            workspace_id: workspaceId,
            narrative_id: response.id
          }
        });
        toast.success(t('Changes saved') as string);
        if (isThreat) {
          navigate(`/threats/${response.id}`);
        } else {
          navigate(`/narratives/${response.id}`);
        }
      } else {
        return response;
      }
    } else {
      ampli.track({
        event_type: 'Case creation',
        event_properties: {
          is_stories_applied: data.createStories,
          is_aspect_based_sentiment_applied: data.isAspectBasedSentimentEnabled,
          workspace_id: workspaceId,
          narrative_id: response.id
        }
      });
      toast.success(t('Changes saved') as string);
      if (isThreat) {
        navigate(`/threats/${response.id}`);
      } else {
        navigate(`/narratives/${response.id}`);
      }
    }
  };

  const handleCopyFromEdit = async (data: NarrativeFormData) => {
    const narrativeTypes = [
      { type: 'NARRATIVE' },
      { type: 'MANUAL' }, 
      { type: 'TOPIC_MODELING' }
    ];
    
    const currentType = narrativeTypes[data.currentNarrativeType]?.type;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (isThreat && narrative?.workspace_id !== currentUser?.workspace?.id) {
      headers['workspace-id'] = narrative?.workspace_id;
    }

    let parameters = null;
    
    let excludingFilters: string[] = [];
    if (data.languagesExclude) excludingFilters.push('languages');
    if (data.countriesExclude) excludingFilters.push('source_origin_country_ids');
    if (data.sourceGroupsExclude) excludingFilters.push('source_group_ids');
    if (data.sourcesExclude) excludingFilters.push('source_ids');
    if (data.affiliationCountriesExclude) excludingFilters.push('source_state_affiliated_country_ids');
    if (data.affiliationTypesExclude && Array.isArray(data.affiliationTypes) && data.affiliationTypes.length > 0) excludingFilters.push('source_affiliation_types');
    if (data.discreditationsExclude) excludingFilters.push('source_discreditated_entity_types');
    if (data.feedsExclude) excludingFilters.push('feed_ids');
    if (data.geoGroupExclude) excludingFilters.push('country_group_ids');
    if (data.showBotsOnly === false) excludingFilters.push('source_bots');

    if (currentType === 'NARRATIVE') {
      parameters = {
        keywords: data.keywords ? [data.keywords] : [],
        examples: [],
        start_date: data.startDate ? format(data.startDate, 'yyyy-LL-dd') : null,
        end_date: data.endDate && !data.isCaseOpen ? format(data.endDate, 'yyyy-LL-dd') : null,
        apply_supervised_classifier: data.applySupervisedClassifier,
        source_types: data.sourceTypes,
        source_group_ids: data.sourceGroups.map((v) => v.value),
        languages: data.languages.map((v) => v.value),
        source_ids: data.sources.map((v) => v.value),
        source_origin_country_ids: data.countries.map((v) => v.value),
        translate_keywords_query: data.isMultilang,
        keywords_query_origin_language: data.isMultilang && data.nativeLang ? data.nativeLang.value : '',
        sentiment: data.sentiment.map(score => score.value),
        content_types: data.contentTypes,
        create_stories: data.createStories,
        stories_candidates_min_threshold: data.createStories ? data.storiesCandidatesMinThreshold : null,
        stories_grouping_max_distance: data.createStories ? data.storiesGroupingMaxDistance : null,
        enable_stories_summarization: data.createStories ? showStoriesParameters ? data.enableStoriesSummarization : true : false,
        stories_summarization_language: data.createStories && data.enableStoriesSummarization && data.storiesTranslationLanguage ? data.storiesTranslationLanguage.value : null,
        source_state_affiliated_all_countries: data.affiliationAll,
        source_state_affiliated_country_ids: data.affiliationAll ? [] : data.affiliationCountries.map(v => v.value),
        source_affiliation_types: Array.isArray(data.affiliationTypes) ? toApiAffiliationTypes(data.affiliationTypes.map((v) => v.value)) : [],
        feed_ids: data.feeds.map(v => v.value),
        source_discreditated_entity_types: data.discreditations.map((v) => v.value),
        source_bots: data.showBotsOnly !== null ? data.showBotsOnly : null,
        aspect_based_sentiment: data.isAspectBasedSentimentEnabled,
        aspect_based_sentiment_object: data.isAspectBasedSentimentEnabled ? data.aspectBasedSentimentObject?.value : null,
        aspect_based_sentiment_subject: data.isAspectBasedSentimentEnabled ? data.aspectBasedSentimentSubject : null,
        ...getCollectCommentsParameters(data),
        exclusive_filters: excludingFilters.length > 0 ? excludingFilters : null,
        stories_vectors_model: data.storiesVectorsModel ? data.storiesVectorsModel.value : null,
        include_comments_to_stories: data.includeCommentsToStories,
        include_chat_messages_to_stories: data.includeChatMessagesToStories,
        country_group_ids: data.geoGroup?.length > 0 ? data.geoGroup : [],
        tags: data.tags?.length > 0 ? data.tags : [],
      };
    }

    const response = await API.fetch('POST', '/API/v1/narratives', headers, {
      name: data.name,
      description: data.description,
      type: currentType === 'MANUAL' ? 'NARRATIVE' : currentType,
      is_manual: currentType === 'MANUAL',
      review_status: data.reviewStatus?.value,
      is_active: data.isActive,
      is_threat: isThreat,
      is_created_by_ucbi_reporter: narrative?.is_created_by_ucbi_reporter,
      incident_type: narrative?.incident_type || null,
      topic: narrative?.topic || null,
      geo_scope: narrative?.geo_scope || null,
      workspace_ids: narrative?.workspace_ids || null,
      project_id: (data.project?.value as number) ?? null,
      on_create: {
        from_narrative_id: narrativeId,
      },
      parameters: parameters,
      confirm_quota_usage: data.confirm_quota_usage,
      is_manually_managed: data.isManuallyManaged,
    }, createTimeoutSignal(120_000));

    if (!data.confirm_quota_usage) {
      if (response?.id) {
        API.fetch('DELETE', `/API/v1/narratives/${narrativeId}`).then(
          () => {
            ampli.track({
              event_type: 'Edit case',
              event_properties: {
                user_id: currentUser?.id,
                workspace_id: currentUser?.workspace_id,
                narrative_id: response.id,
                is_stories_applied: data.createStories,
                is_aspect_based_sentiment_applied: data.isAspectBasedSentimentEnabled,
              },
            });
            toast.success(t('Changes saved') as string);
            if (isThreat) {
              navigate(`/threats/${response.id}`);
            } else {
              navigate(`/narratives/${response.id}`);
            }
          },
        ).catch((e) => showRequestError(e, t('Delete case')));
      } else {
        return response;
      }
    } else {
      API.fetch('DELETE', `/API/v1/narratives/${narrativeId}`).then(() => {
        ampli.track({
          event_type: 'Edit case',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            narrative_id: response.id,
            is_stories_applied: data.createStories,
            is_aspect_based_sentiment_applied: data.isAspectBasedSentimentEnabled,
          },
        });
        toast.success(t('Changes saved') as string);
        if (isThreat) {
          navigate(`/threats/${response.id}`);
        } else {
          navigate(`/narratives/${response.id}`);
        }
      }).catch((e) => showRequestError(e, t('Delete case')));
    }
  };

  const handleSubmit = async (data: NarrativeFormData) => {
    try {
      if (!data.refreshWholeCase) {
        return await handleEdit(data);
      } else {
        return await handleCopyFromEdit(data);
      }
    } catch (error: unknown) {
      showRequestError(error, t('Edit case'));
    }
  };

  if (loading) {
    return <div><Loader/></div>;
  }

  if (!initialData) {
    return <div className='noData'>{t('Page not found...')}</div>;
  }

  return (
    <NarrativeForm
      mode="edit"
      narrativeId={narrativeId}
      initialData={initialData}
      onSubmit={handleSubmit}
    />
  );
}